import { FC, useEffect, useState, useCallback } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { MainLayout } from '../MainLayout';
import { DataTable } from 'src/components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { trpc } from 'src/trpc';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'src/components/Search';
import { Loader } from 'src/components/Loader';
import { NewCleanupModal } from './NewCleanupSheet';
import { CleanupColumns } from './CleanupColumns';
import { useDebounce } from 'src/components/Debounce';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganization } from 'src/context/OrganizationContext';

export const CleanupDashboard: FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [params, setParams] = useSearchParams({
    offset: '0',
    limit: '10',
    search: '',
    sortDirection: 'asc',
    sortKey: 'position',
    archived: 'all',
  });

  const [search, setSearch] = useState(params.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const offset = parseInt(params.get('offset') ?? '0');
  const limit = parseInt(params.get('limit') ?? '10');
  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const { data, isLoading, isPending, isFetching } = trpc.admin.cleanup.getCleanups.useQuery(
    {
      offset,
      limit,
      search: params.get('search') ?? '',
      sortDirection: (params.get('sortDirection') ?? 'asc') as 'asc' | 'desc',
      sortKey: params.get('sortKey')?.trim() || 'position',
      archived: (params.get('archived') ?? 'all') as 'active' | 'archived' | 'all',
    },
    {
      placeholderData: keepPreviousData,
      enabled: isResolved && !!organizationId,
    },
  );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  const updateSearchParams = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(params);
      newParams.set('search', searchValue);
      newParams.set('offset', '0');
      setParams(newParams);
    },
    [params, setParams],
  );

  useEffect(() => {
    const currentSearch = params.get('search') ?? '';
    if (debouncedSearch !== currentSearch) {
      updateSearchParams(debouncedSearch);
    }
  }, [debouncedSearch, params, updateSearchParams]);

  useEffect(() => {
    if (sorting.length) {
      const newParams = new URLSearchParams(params);
      newParams.set('sortDirection', sorting[0].desc ? 'desc' : 'asc');
      newParams.set('sortKey', sorting[0].id || 'position');
      newParams.set('offset', '0');
      setParams(newParams);
    }
  }, [sorting, params, setParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleArchivedFilter = (value: string) => {
    const newParams = new URLSearchParams(params);
    newParams.set('archived', value);
    newParams.set('offset', '0'); // Reset to first page
    setParams(newParams);
  };

  const displayData = data?.cleanups || [];
  const total = data?.total || 0;

  if (!isResolved || isOrganizationSwitching || (isPending && isFetching && !data)) {
    return (
      <MainLayout>
        <Loader title="Loading cleanups" />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-semibold">Cleanup Services</h2>
          <NewCleanupModal />
        </div>

        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <Search
              value={search}
              onChange={handleSearch}
              placeholder="Search cleanups by title or description"
              isLoading={isLoading}
            />
          </div>
          <div className="w-48">
            <Select value={params.get('archived') ?? 'all'} onValueChange={handleArchivedFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cleanups</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="archived">Archived Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col h-full">
          <DataTable
            columns={CleanupColumns}
            data={displayData}
            currentPage={currentPage}
            sorting={sorting}
            onSortingChange={setSorting}
            hasNextPage={total > offset + limit}
            hasPreviousPage={offset > 0}
            onPageChange={(change) => {
              const newOffset = Math.max(0, offset + change * limit);
              setParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set('offset', newOffset.toString());
                return newParams;
              });
            }}
            pageCount={Math.ceil(total / limit)}
            total={total}
            resourceName="cleanups"
            offset={offset}
            limit={limit}
          />
        </div>
      </div>
    </MainLayout>
  );
};
